"use client"

import { useState, useEffect } from "react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  Eye,
  MoreHorizontal,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
} from "lucide-react"
import { Badge } from "../ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog"
import { adminDashboard, formateurDashboard } from "../../services/api"
import { useAuth } from '../../context/AuthContext';

// Types
interface Formation {
  id: number
  image: string | null
  name: string
  category: string
  totalDuration: string
  status: 'Publié' | 'Archivé'
  inscriptionCount: number
  chapterCount: number
}

interface Category {
  id: number
  name: string
}

interface ApiResponse {
  success: boolean
  data: Formation[]
  categories: Category[]
}

export default function FormateurFormations() {
  const { user } = useAuth();

  const [searchTerm, setSearchTerm] = useState("")
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Dynamic data states
  const [formations, setFormations] = useState<Formation[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [formationToDelete, setFormationToDelete] = useState<number | null>(null)

  // Fetch formations and categories
  useEffect(() => {
  const fetchData = async () => {
    // Check if user exists and has an ID
    if (!user || !user.id) {
      setError("Utilisateur non authentifié");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await formateurDashboard.getFormateurFormationsOverview(user.id);

      if (response.data.success) {
        setFormations(response.data.data);
        setCategories(response.data.categories);
        setError(null); // Clear any previous errors
      } else {
        setError("Erreur lors du chargement des formations");
      }
    } catch (err) {
      console.error("Erreur lors du chargement:", err);
      setError("Erreur lors du chargement des formations");
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [user?.id]);

  // Delete formation handler
  const handleDeleteFormation = async (formationId: number) => {
    setFormationToDelete(formationId)
    setDeleteDialogOpen(true)
  }

  // Confirm delete formation
  const confirmDeleteFormation = async () => {
    if (!formationToDelete) return

    try {
      const response = await adminDashboard.deleteFormation(formationToDelete)

      if (response.data.success) {
        // Remove formation from local state
        setFormations(prev => prev.filter(formation => formation.id !== formationToDelete))
        alert("Formation supprimée avec succès")
      } else {
        alert("Erreur lors de la suppression de la formation")
      }
    } catch (err) {
      console.error("Erreur lors de la suppression:", err)
      alert("Erreur lors de la suppression de la formation")
    } finally {
      setDeleteDialogOpen(false)
      setFormationToDelete(null)
    }
  }

  // Filter formations
  const filteredFormations = formations.filter((formation) => {
    // Filter by search term
    const matchesSearch =
      formation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      "Elille academy".toLowerCase().includes(searchTerm.toLowerCase())

    // Filter by category
    const matchesCategory = categoryFilter === null || categoryFilter === "all" || formation.category === categoryFilter

    // Filter by status
    const matchesStatus = statusFilter === null || statusFilter === "all" || formation.status === statusFilter

    return matchesSearch && matchesCategory && matchesStatus
  })

  // Pagination
  const totalPages = Math.ceil(filteredFormations.length / itemsPerPage)
  const paginatedFormations = filteredFormations.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, categoryFilter, statusFilter])

  // Helper function to get status display text and styling
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'Publié':
        return {
          text: 'Publié',
          className: 'bg-green-50 text-green-700 border-green-200'
        }
      case 'Archivé':
        return {
          text: 'Archivé',
          className: 'bg-gray-50 text-gray-700 border-gray-200'
        }
      default:
        return {
          text: status,
          className: 'bg-gray-50 text-gray-700 border-gray-200'
        }
    }
  }

  if (loading) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p>Chargement des formations...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Réessayer</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container px-4 py-8 md:px-6 md:py-12">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-serif font-bold tracking-tight">Gestion des formations</h1>
            <p className="text-gray-500 dark:text-gray-400">
              Créez, modifiez et gérez les formations disponibles sur la plateforme.
            </p>
          </div>
          <div className="flex gap-2">
            <a href="/formateur/courses/new">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Ajouter une formation
              </Button>
            </a>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Liste des formations</CardTitle>
            <CardDescription>{filteredFormations.length} formations trouvées</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <Input
                    type="search"
                    placeholder="Rechercher..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={categoryFilter || "all"} onValueChange={(value) => setCategoryFilter(value === "all" ? null : value)}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Toutes les catégories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les catégories</SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.name}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? null : value)}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Tous les statuts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="Publié">Publié</SelectItem>
                      <SelectItem value="Archivé">Archivé</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon">
                    <SlidersHorizontal className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">Afficher</p>
                <Select value={String(itemsPerPage)} onValueChange={(value) => setItemsPerPage(Number(value))}>
                  <SelectTrigger className="w-[80px]">
                    <SelectValue placeholder={itemsPerPage} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">par page</p>
              </div>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Formation</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Formateur</TableHead>
                    <TableHead>Inscrits</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedFormations.length > 0 ? (
                    paginatedFormations.map((formation) => {
                      const statusDisplay = getStatusDisplay(formation.status)

                      return (
                        <TableRow key={formation.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="relative h-12 w-12 flex-shrink-0">
                                <img
                                  src={formation.image || "/placeholder.svg"}
                                  alt={formation.name}
                                  className="rounded-md object-cover w-full h-full absolute inset-0"
                                />
                              </div>
                              <div>
                                <div className="font-medium">{formation.name}</div>
                                <div className="text-sm text-muted-foreground">
                                  {formation.totalDuration} • {formation.chapterCount} modules
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{formation.category}</Badge>
                          </TableCell>
                          <TableCell>Elille academy</TableCell>
                          <TableCell>{formation.inscriptionCount}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              style={{
                                backgroundColor: formation.status === 'Publié' ? '#f0fdf4' : '#f9fafb',
                                color: formation.status === 'Publié' ? '#15803d' : '#374151',
                                borderColor: formation.status === 'Publié' ? '#bbf7d0' : '#d1d5db'
                              }}
                            >
                              {statusDisplay.text}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                  <span className="sr-only">Menu</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <a href={`/courses/${formation.id}`}>
                                  <DropdownMenuItem>
                                      <Eye className="mr-2 h-4 w-4" />
                                      Voir
                                  </DropdownMenuItem>
                                </a>
                                <a href={`/formateur/courses/${formation.id}`}>
                                  <DropdownMenuItem>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Modifier
                                  </DropdownMenuItem>
                                </a>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleDeleteFormation(formation.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                        Aucune formation trouvée
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Affichage de {(currentPage - 1) * itemsPerPage + 1} à{" "}
                  {Math.min(currentPage * itemsPerPage, filteredFormations.length)} sur {filteredFormations.length} formations
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="text-sm">
                    Page {currentPage} sur {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la formation</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cette formation ? Cette action est irréversible et supprimera également toutes les données associées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteFormation}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
